import { useStytch, useStytchSession } from "@stytch/react";
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router";
import { useAtom } from "jotai";
import { useCallback, useEffect, useRef, useState, type JSX } from "react";
import { z } from "zod";

import { AuthPageLayout } from "@/components/auth/auth-page-layout";
import { AuthFlowContinueButton } from "@/components/ui/auth-flow/auth-flow-continue-button";
import { AuthFlowTitle } from "@/components/ui/auth-flow/auth-flow-title";
import { ErrorDrawer } from "@/components/ui/drawer/error-drawer";
import { FlowHeader } from "@/components/ui/flow-header/flow-header";
import { InputOtp } from "@/components/ui/input/input-otp";
import { useSendEmailOtp } from "@/hooks/use-send-email-otp";
import { firstAccessAtom } from "@/lib/atoms/first-access-atom";
import {
  captureAuthError,
  trackFirstAccessOtpResent,
  trackFirstAccessOtpSendFailed,
  trackFirstAccessOtpVerified,
  trackFirstAccessStepViewed,
} from "@/lib/auth/auth-tracking";
import { guardFirstAccessDocument } from "@/lib/guards/first-access-guard";
import { generateLivanceContactUrl } from "@/lib/helpers/contact-url";
import { isSessionFreshForPasswordReset } from "@/lib/utils/stytch-session";

const OTP_LENGTH = 6;
const RESEND_COUNTDOWN_SECONDS = 120;
const OTP_SESSION_DURATION_MINUTES = 60;

const FirstAccessVerifyEmailPage = (): JSX.Element => {
  const navigate = useNavigate();
  const router = useRouter();
  const searchParams = useSearch({ from: "/auth/first-access/verify-email" });
  const stytch = useStytch();

  const [firstAccess, setFirstAccess] = useAtom(firstAccessAtom);

  const [otp, setOtp] = useState("");
  const [countdown, setCountdown] = useState(RESEND_COUNTDOWN_SECONDS);
  const [isVerifying, setIsVerifying] = useState(false);

  const { session } = useStytchSession();

  const isOtpComplete = otp.length === OTP_LENGTH;
  const canResend = countdown === 0;

  const { sendOtp: sendEmailOtp } = useSendEmailOtp();

  useEffect(() => {
    trackFirstAccessStepViewed("verificacao-email");
  }, []);

  const sendOtp = useCallback(async (): Promise<void> => {
    try {
      const response = await sendEmailOtp(firstAccess.document);

      setFirstAccess((prev) => ({ ...prev, methodId: response.methodId }));
    } catch (error) {
      // Mantém a falha não-bloqueante (o usuário pode reenviar), mas registra o sinal.
      captureAuthError(error, { flow: "primeiro-acesso", step: "verificacao-email" });
      trackFirstAccessOtpSendFailed(error);
    }
  }, [sendEmailOtp, firstAccess.document, setFirstAccess]);

  // Redireciona diretamente se já houver sessão ativa e recente (< 5 min)
  // Se a sessão existir mas estiver expirada, o fluxo do OTP cria um novo fator
  useEffect(() => {
    if (session && isSessionFreshForPasswordReset(session)) {
      void navigate({ to: "/auth/first-access/define-password" });
    }
  }, [session, navigate]);

  useEffect(() => {
    if (countdown <= 0) {
      return;
    }
    const timer = setInterval(() => setCountdown((prev) => prev - 1), 1000);
    return () => clearInterval(timer);
  }, [countdown]);

  const handleTalkToTeam = (): void => {
    const url = generateLivanceContactUrl("whatsapp");

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatCountdown = (seconds: number): string => {
    const m = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const s = (seconds % 60).toString().padStart(2, "0");
    return `${m}:${s}`;
  };

  const handleResend = (): void => {
    trackFirstAccessOtpResent();
    setCountdown(RESEND_COUNTDOWN_SECONDS);
    setOtp("");
    hideOtpErrorDrawer();
    void sendOtp();
  };

  const showOtpErrorDrawer = useCallback((): void => {
    void navigate({
      to: "/auth/first-access/verify-email",
      search: { action: "error" },
    });
  }, [navigate]);

  const hideOtpErrorDrawer = useCallback((): void => {
    if (searchParams.action === "error") {
      router.history.back();
    }
  }, [searchParams.action, router]);

  const handleContinue = useCallback(async (): Promise<void> => {
    if (!isOtpComplete || isVerifying) return;

    setIsVerifying(true);

    try {
      await stytch.otps.authenticate(otp, firstAccess.methodId ?? "", {
        session_duration_minutes: OTP_SESSION_DURATION_MINUTES,
      });

      trackFirstAccessOtpVerified("sucesso");
      void navigate({ to: "/auth/first-access/define-password" });
    } catch (error) {
      trackFirstAccessOtpVerified("falha", error);
      captureAuthError(error, { flow: "primeiro-acesso", step: "verificacao-email" });
      setOtp("");
      showOtpErrorDrawer();
    } finally {
      setIsVerifying(false);
    }
  }, [
    isOtpComplete,
    isVerifying,
    stytch,
    otp,
    firstAccess.methodId,
    navigate,
    showOtpErrorDrawer,
  ]);

  // Mantém uma ref estável para que o efeito abaixo só rode novamente quando
  // isOtpComplete mudar, e não quando handleContinue mudar de identidade
  // (por exemplo, após uma navegação).
  const handleContinueRef = useRef(handleContinue);
  handleContinueRef.current = handleContinue;

  useEffect(() => {
    if (isOtpComplete) {
      void handleContinueRef.current();
    }
  }, [isOtpComplete]);

  return (
    <AuthPageLayout>
      <FlowHeader>
        <FlowHeader.Actions>
          <FlowHeader.BackButton />
          <FlowHeader.HelpButton />
        </FlowHeader.Actions>
        <FlowHeader.ProgressBar progress={0.5} />
      </FlowHeader>

      {/* Page content */}
      <div className="flex flex-col flex-1 p-6 gap-6 w-full">
        <AuthFlowTitle>
          <AuthFlowTitle.Heading>
            Insira o código que enviamos para o e-mail
          </AuthFlowTitle.Heading>
          <AuthFlowTitle.Sub className="text-primary-400 font-medium text-md">
            {firstAccess.email}
          </AuthFlowTitle.Sub>
        </AuthFlowTitle>

        <div className="flex flex-col gap-4">
          <InputOtp value={otp} onChange={setOtp} />

          <div className="text-center text-sm">
            {canResend ? (
              <button
                type="button"
                onClick={handleResend}
                className="text-primary-400 font-medium"
              >
                Reenviar código
              </button>
            ) : (
              <span className="text-neutral-500">
                Reenviar código em ({formatCountdown(countdown)})
              </span>
            )}
          </div>
        </div>

        <div className="flex-1" />

        <div className="text-center text-sm text-neutral-500">
          E-mail incorreto?{" "}
          <button
            type="button"
            className="text-secondary-600 underline font-medium"
            onClick={handleTalkToTeam}
          >
            Solicitar alteração
          </button>
        </div>

        <AuthFlowContinueButton
          loading={isVerifying}
          disabled={!isOtpComplete}
          onClick={() => void handleContinue()}
        />
      </div>

      <ErrorDrawer
        message="Código inválido ou expirado. Tente novamente."
        open={searchParams.action === "error"}
        setOpen={(isOpen) => {
          if (isOpen) showOtpErrorDrawer();
          else hideOtpErrorDrawer();
        }}
      />
    </AuthPageLayout>
  );
};

const firstAccessVerifyEmailSearchSchema = z.object({
  action: z.string().nullable().optional(),
});

export const Route = createFileRoute("/auth/first-access/verify-email")({
  beforeLoad: () => {
    guardFirstAccessDocument();
  },
  component: FirstAccessVerifyEmailPage,
  validateSearch: firstAccessVerifyEmailSearchSchema,
});
